import { ImproveTemplateDto } from "../../../Types/Improve/ImproveTemplateDto";
import { formatUkDateSafe } from "../../../Helpers/Dates";
import { UserDisplay } from "../../../Types/User";
import type { ImproveTaskDetail } from "../../../Types/Improve/ImprovementPlanDetail";
import {
  IMPROVE_EXPORT_COLS,
  ImproveAreaExportData,
  ImproveExportColumnKey,
  ImproveExportFieldVisibility,
  fullImproveExportFieldVisibility,
} from "./types";
import { getImproveExportColumnLabel } from "./improveExportTemplateFields";

export function assignedToNames(users: UserDisplay[] | undefined): string {
  if (!users?.length) return "";
  return users.map((u) => u.fullName || u.sub).filter(Boolean).join(", ");
}

export function taskAssigneeName(u: ImproveTaskDetail | undefined): string {
  if (!u?.assignedTo) return "";
  return u.assignedTo.fullName || u.assignedTo.sub || "";
}

export function tagsJoin(tags: string[] | undefined): string {
  if (!tags?.length) return "";
  return tags.join(", ");
}

export function formatUrlCell(
  url: string | undefined,
  text: string | undefined
): string {
  const u = (url ?? "").trim();
  const t = (text ?? "").trim();
  if (t && u) return `${t} (${u})`;
  if (u) return u;
  return t;
}

function rowFromValues(
  values: Record<ImproveExportColumnKey, string>,
  vis: ImproveExportFieldVisibility
): string[] {
  return IMPROVE_EXPORT_COLS.filter((k) => vis[k]).map((k) => values[k] ?? "");
}

export function buildImproveExportHeader(
  vis: ImproveExportFieldVisibility,
  templates: ImproveTemplateDto[] = []
): string[] {
  return IMPROVE_EXPORT_COLS.filter((k) => vis[k]).map((k) =>
    getImproveExportColumnLabel(k, templates)
  );
}

/**
 * One row per task; improvements with no tasks emit one row with empty task columns.
 */
export function buildImproveExportDataRows(
  areas: ImproveAreaExportData[],
  fieldVisibility?: Partial<ImproveExportFieldVisibility>
): string[][] {
  const vis = fullImproveExportFieldVisibility(fieldVisibility);
  const rows: string[][] = [];

  for (const area of areas) {
    const planTitle = area.title ?? "";
    const due = formatUkDateSafe(area.endDate);
    const owner = area.ownerName ?? "";
    const planStatus = area.status ?? "";
    const improvements = area.improvements ?? [];

    for (const imp of improvements) {
      const tasks =
        imp.tasks?.length ? imp.tasks : [undefined as ImproveTaskDetail | undefined];
      for (const task of tasks) {
        const values: Record<ImproveExportColumnKey, string> = {
          "Plan title": planTitle,
          "Due date": due,
          Owner: owner,
          "Plan status": planStatus,
          Objective: imp.title ?? "",
          "Improvement status": imp.status ?? "",
          "Assigned to": assignedToNames(imp.assignedTo),
          "Target date": formatUkDateSafe(imp.targetDate),
          "Completion date": formatUkDateSafe(imp.completionDate),
          "Expected impact": imp.expectedImpact ?? "",
          "Actual impact": imp.actualImpact ?? "",
          Tags: tagsJoin(imp.tags),
          "Custom tags": imp.customTags ?? "",
          "Custom date": formatUkDateSafe(imp.customDate),
          "Column A": imp.customTextA ?? "",
          "Column B": imp.customTextB ?? "",
          "Column C": imp.customTextC ?? "",
          "URL 1": formatUrlCell(imp.uRL1, imp.uRL1Text),
          "URL 2": formatUrlCell(imp.uRL2, imp.uRL2Text),
          "URL 3": formatUrlCell(imp.uRL3, imp.uRL3Text),
          "Task heading": task?.taskHeading ?? "",
          "Task status": task?.taskStatus ?? "",
          "Task target date": formatUkDateSafe(task?.targetDate),
          "Task completion date": formatUkDateSafe(task?.completionDate),
          "Task assigned to": task ? taskAssigneeName(task) : "",
          "Column D": task?.customTextD ?? "",
          "Column E": task?.customTextE ?? "",
          "Column F": task?.customTextF ?? "",
          "Task custom tags": task?.customTagsB ?? "",
        };
        rows.push(rowFromValues(values, vis));
      }
    }

    if (improvements.length === 0) {
      const values: Record<ImproveExportColumnKey, string> = {
        "Plan title": planTitle,
        "Due date": due,
        Owner: owner,
        "Plan status": planStatus,
        Objective: "",
        "Improvement status": "",
        "Assigned to": "",
        "Target date": "",
        "Completion date": "",
        "Expected impact": "",
        "Actual impact": "",
        Tags: "",
        "Custom tags": "",
        "Custom date": "",
        "Column A": "",
        "Column B": "",
        "Column C": "",
        "URL 1": "",
        "URL 2": "",
        "URL 3": "",
        "Task heading": "",
        "Task status": "",
        "Task target date": "",
        "Task completion date": "",
        "Task assigned to": "",
        "Column D": "",
        "Column E": "",
        "Column F": "",
        "Task custom tags": "",
      };
      rows.push(rowFromValues(values, vis));
    }
  }

  return rows;
}
